import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import { AddComment as NewChatIcon, Lock as LockIcon } from '@mui/icons-material';
import { ConversationHistoryItem } from '../types';

interface ConversationHistoryPanelProps {
  items: ConversationHistoryItem[];
  activeId: string | null;
  onSelect: (conversationId: string) => void;
  onNewChat: () => void;
  loading?: boolean;
  title?: string;
  /** Email of the currently logged-in user. Items from other users are shown as read-only. */
  currentUserEmail?: string | null;
}

function formatDateTime(isoString: string): string {
  // Python's isoformat() emits naive datetimes without a timezone suffix.
  // Append 'Z' when missing so the browser always parses it as UTC,
  // then toLocaleString() converts to the browser's local timezone.
  const hasTimezone = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString);
  const date = new Date(hasTimezone ? isoString : isoString + 'Z');
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ConversationHistoryPanel: React.FC<ConversationHistoryPanelProps> = ({
  items,
  activeId,
  onSelect,
  onNewChat,
  loading = false,
  title = 'History',
  currentUserEmail,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        minWidth: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'grey.50',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Tooltip title="Start new chat" placement="right" arrow>
          <Button
            size="small"
            startIcon={<NewChatIcon fontSize="small" />}
            onClick={onNewChat}
            variant="text"
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              px: 0.75,
              py: 0.25,
              minWidth: 'auto',
              color: 'primary.main',
            }}
          >
            New
          </Button>
        </Tooltip>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 1.5, pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              No past conversations yet.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {items.map((item, index) => {
              const isOwn =
                !currentUserEmail ||
                !item.user_email ||
                item.user_email === currentUserEmail;
              const isActive = item.conversation_external_id === activeId;

              return (
                <React.Fragment key={item.conversation_external_id}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => onSelect(item.conversation_external_id)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.50',
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'primary.100',
                      },
                    }}
                  >
                    {/* Datetime + badges on one row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', flexWrap: 'wrap' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: isActive ? 600 : 400,
                          color: item.error_message ? 'error.main' : 'text.primary',
                          flexShrink: 0,
                        }}
                      >
                        {item.created_at ? formatDateTime(item.created_at) : 'Unknown date'}
                      </Typography>

                      {!isOwn && (
                        <Tooltip
                          title={`Created by ${item.user_email ?? 'another user'} — read-only`}
                          placement="right"
                          arrow
                        >
                          <LockIcon sx={{ fontSize: 12, color: 'warning.dark', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      {item.error_message && (
                        <Chip
                          label="Error"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </ListItemButton>
                  {index < items.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ConversationHistoryPanel;
